import { FileDiff, Gauge, Boxes, Car, Syringe } from "lucide-react";
import { Callout, Tag } from "@/components/GuidesShared";
import { StepList, BulletList } from "@/lib/guides/primitives";
import type { GuideSectionDef } from "@/lib/guides/types";

export const newPaidToolSections: GuideSectionDef[] = [
  {
    id: "handling-diff",
    label: "Handling Diff",
    icon: <FileDiff className="h-5 w-5" />,
    shortDescription: "Compare two handling.meta files as physics, not as text.",
    seoTitle: "Handling Diff — Compare FiveM handling.meta Files",
    seoDescription:
      "Compare two handling.meta files field by field. Float noise is ignored, flag bits and sub-handling are counted, and misspelled fields are listed instead of hidden.",
    keywords: [
      "handling.meta diff",
      "compare handling.meta",
      "FiveM handling compare",
      "GTA5 handling editor",
      "handling.meta checker",
    ],
    toolHref: "/handling-diff",
    faqs: [
      {
        q: "How is this different from a text diff?",
        a: "It reads each handling.meta as vehicle physics. Reordered lines and float noise like 1.0 versus 1.000000 are not reported as changes, while a changed flag bit or sub-handling value is.",
      },
      {
        q: "What if the files hold several vehicles?",
        a: "Vehicles are paired by handlingName, and a badge shows whether the pairing was automatic or one you chose. If the two names differ, the page says so above the rows.",
      },
      {
        q: "What does 'not recognised' mean?",
        a: "Any element in either file that no handling field claims, such as a misspelled tag. The game ignores these silently, so the tool lists them rather than showing a default.",
      },
      {
        q: "Is my handling.meta uploaded anywhere?",
        a: "No. Both sides are read and compared in your browser tab.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Handling Diff compares two <Tag>handling.meta</Tag> documents as physics records. Use
          it to see exactly what an edit, a vendor update or a tuning pass changed in a vehicle,
          without wading through reordered lines and rounding differences. It is included with the
          Vehicle Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Load side A, the file you started from, and side B, the edited one. Drop a file or paste the XML into either side.",
            "If a file holds several vehicles, check the match badge. Entries are paired by handlingName, and you can pick a different entry on either side.",
            "Read the headline count, which is split into fields, flags and sub-handling, then the rows grouped by category. Sort by biggest relative move if you prefer.",
            "Check the list of unrecognised elements for typos, then use Copy as Markdown to paste the result into a ticket or changelog.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it reports</p>
        <BulletList
          items={[
            "Changed fields, only where the difference clears that field's threshold. Smaller differences are shown separately as below the change threshold while Changed only is on, which is the default.",
            "Flag bits, including a sub-handling block's own flags, counted in the headline.",
            "Vehicles that exist in only one of the two files.",
          ]}
        />
        <Callout type="tip">
          Always read the unrecognised list before trusting a clean result. A misspelled field
          does nothing in game, and a diff that skips it will tell you nothing changed.
        </Callout>
      </>
    ),
  },
  {
    id: "resmon-report",
    label: "Resmon Report",
    icon: <Gauge className="h-5 w-5" />,
    shortDescription: "Rank a resmon dump by per-frame CPU and compare before and after.",
    seoTitle: "Resmon Report — Analyse FiveM resmon Dumps",
    seoDescription:
      "Paste a resmon dump from the in-game overlay, txAdmin, console, CSV or JSON. Rank resources by per-frame CPU, flag the heavy ones, and compare two captures.",
    keywords: [
      "FiveM resmon analyzer",
      "resmon report",
      "FiveM performance",
      "txAdmin resmon",
      "FiveM resource CPU ms",
    ],
    toolHref: "/resmon",
    faqs: [
      {
        q: "Which resmon formats can I paste?",
        a: "The in-game overlay, a txAdmin table, a console dump, a CSV or a JSON export. You can paste the text or drop a .txt, .log, .csv or .json file.",
      },
      {
        q: "What counts as a heavy resource?",
        a: "Per-frame CPU is ranked worst first. A resource at 1 ms or more is marked as worth a look, and 2 ms or more as high. The figure is per frame, and the whole frame at 60 fps is about 16 ms shared by every resource.",
      },
      {
        q: "What happens to lines the tool cannot read?",
        a: "They are listed with their raw text and the reason they were skipped, not dropped. The sum of the rows is also checked against the total the dump printed, and a mismatch is shown as a warning.",
      },
      {
        q: "Does it upload my dump or keep it?",
        a: "Nothing is uploaded. The two capture slots are saved in your browser's local storage so a reload does not lose your baseline, unless A and B together run past about a million characters or the browser refuses storage. Then they are not kept, and the page says so.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Resmon Report turns a pasted resmon dump into a table ranked by per-frame CPU time, so
          you can see which resources are costing you and whether a change actually helped. It is included
          with the Server Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Capture a resmon dump on your server before you change anything and paste it into slot A.",
            "Make the change, capture again, and paste it into slot B.",
            "Read each slot's table, ranked worst first, with the severity bands marked. Check the row total against the dump's own total.",
            "With both slots filled, the difference A to B section under the two panels shows each resource's move between A and B. Export CSV from either slot or from the difference.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it checks</p>
        <BulletList
          items={[
            "Rows read against the total the dump printed, with a warning when they disagree.",
            "Every unreadable line, listed with its raw text.",
            "Resources matched between captures by name, ignoring capitalisation, with a change under 0.005 ms not counted as a change.",
          ]}
        />
        <Callout type="tip">
          Capture A and B under similar conditions, at a similar player count and after the
          same uptime. A heavy resource at 3 a.m. and the same resource at peak hours are not
          comparable numbers.
        </Callout>
      </>
    ),
  },
  {
    id: "asset-directory",
    label: "Asset Directory",
    icon: <Boxes className="h-5 w-5" />,
    shortDescription: "Browse vanilla GTA V vehicles, peds and clothing with their DLC pack.",
    seoTitle: "Asset Directory — Vanilla GTA V Vehicles, Peds and Clothing",
    seoDescription:
      "Search every vanilla GTA V vehicle, ped and freemode clothing item by spawn name, with DLC pack and release date, untextured 3D previews and, for vehicles and peds, a spawn snippet.",
    keywords: [
      "GTA V vehicle list",
      "GTA5 spawn names",
      "vanilla ped list",
      "GTA V DLC pack list",
      "FiveM asset directory",
    ],
    toolHref: "/assets",
    faqs: [
      {
        q: "What is in the directory?",
        a: "Vanilla GTA V vehicles, peds and freemode clothing, each with the DLC pack it ships in and a release date where one is known.",
      },
      {
        q: "How do I search?",
        a: "Type a spawn name or part of one; for clothing, type part of the Rockstar identifier, such as lts or uppr. Names are matched as typed, so a partial spawn name works but a display name does not. You can also narrow by group and by DLC pack.",
      },
      {
        q: "Are the 3D previews the real models?",
        a: "No. They are untextured grey geometry, decimated, and they only appear where one has been baked. Vehicle previews do not include wheels.",
      },
      {
        q: "Does a name check tell me a name is safe?",
        a: "No. The detail page can check a name against the vanilla names, but a miss only means no collision was found for the build the catalogue was baked from. A later DLC could still use it.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Asset Directory is a baked catalogue of what ships with GTA V. Use it to look up a
          spawn name, find which DLC pack an item came from, or check what already exists
          before you name your own asset. It is included with the Vehicle Developer, Server
          Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            "Pick Vehicles, Peds or Clothing. Each section loads the first time you open it.",
            "Search by spawn name, or by the Rockstar identifier for clothing, and narrow with the group and DLC pack selectors. A broad search shows the first slice and the total matched, so narrow it to reach the rest.",
            "Open an item for its detail page: DLC pack and release date. Vehicles and peds also have a spawn snippet you can copy; clothing pages show the identifier, packs and outfits instead.",
            "Use the name check on the detail page to test a name against the stock files. On a vehicle's page, Open in Easy Meta carries on into the meta tools.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Good to know</p>
        <BulletList
          items={[
            "The page prints the game build and bake date. Anything added by a newer DLC will not be listed.",
            "Open in UV Studio needs your own copy of the vehicle's .yft, because the preview is decimated and untextured.",
          ]}
        />
        <Callout type="tip">
          Treat a name check with no hit as a prompt to look further, not a clearance. It
          reports that no collision was found for this build, not that the name is free.
        </Callout>
      </>
    ),
  },
  {
    id: "modkit-viewer",
    label: "Modkit Viewer",
    icon: <Car className="h-5 w-5" />,
    shortDescription: "Preview a vehicle's kit parts, extras, doors and textures in 3D. Files never leave your device.",
    seoTitle: "Modkit Viewer — Preview FiveM Vehicle Mod Kits in 3D",
    seoDescription:
      "Open a FiveM vehicle resource and try its modkit parts, extras, doors and textures on the real model. Save builds as JSON. Your files stay on your computer.",
    keywords: [
      "FiveM modkit viewer",
      "vehicle mod kit preview",
      "carvariations.meta kit",
      "FiveM vehicle extras",
      "GTA5 vehicle parts preview",
    ],
    toolHref: "/modkit-viewer",
    faqs: [
      {
        q: "Which files do I open?",
        a: "Open the resource folder that holds the vehicle's .yft, its part .yft files, the .ytd texture dictionaries, and its carcols.meta and carvariations.meta. Open files accepts the same files loose.",
      },
      {
        q: "Why is the car untextured?",
        a: "The textures may be in a .ytd the viewer could not match to the vehicle. Click Load YTD in the Appearance panel, or drop a .ytd onto the preview, and the textures are applied without reloading the model.",
      },
      {
        q: "Can I open a ZIP or RPF?",
        a: "Not directly. Extract it first, then open the resulting folder. The viewer reads one resource at a time.",
      },
      {
        q: "Are my files uploaded?",
        a: "No. Models and textures are read in your browser and never sent to a server. Your original files are not changed.",
      },
      {
        q: "A door opens the wrong way. Can I fix it?",
        a: "Yes. Open Motion settings in the Doors panel and pick another opening motion, such as gullwing, scissor or a slide. Reverse direction flips it. Door movement is worked out from the model's shape, so an unusual door may need a manual choice.",
      },
      {
        q: "Who can use Modkit Viewer?",
        a: "It is included with the Vehicle Developer plan and above, which means Vehicle Developer, Pro, and Studio.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Modkit Viewer shows a vehicle with its mod kit on the real model. Pick a part for each
          category, toggle extras, open the doors and check the textures, all without starting
          your server. It is included with the Vehicle Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Click Open resource and choose the extracted resource folder. Or use Open files, or drop the .yft, .ytd and meta files onto the preview.",
            "In Vehicle / import, choose the Vehicle and its Mod kit.",
            "In Parts, choose a part for each category. Pick Stock to go back to the original piece for that category, or All stock to reset them all.",
            "In Extras, switch each extra on or off, or use All on / All off.",
            "In Doors, use Open all / Close all, or open them one at a time. Use Motion settings if a door swings the wrong way.",
            "If the textures are missing, use Load YTD in Appearance. You can also pick a paint color there.",
            "Name the build and save it under Saved builds. Use Reset view to frame the car again.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Good to know</p>
        <BulletList
          items={[
            "Everything stays in your browser. Open one resource at a time, and extract ZIP or RPF archives first.",
            "Stock geometry is hidden only when every replacement file for that selection is found. Missing or duplicate filenames show as warnings instead of loading a file from another resource.",
            "Your camera stays where it is while you change parts, extras or textures. Opening another vehicle fits it automatically.",
            "Saved builds are kept in this browser for each vehicle and kit. JSON export and import carry your part choices and disabled extras, not the model files. A build made for another vehicle or kit is rejected.",
            "Custom door bone lets you add a door on a non-standard bone, such as an add-on door. Choose the bone, click Add custom door, then set its Opening motion.",
          ]}
        />
        <Callout type="tip">
          Door and extra motion is worked out from the model's shape, not from the game's own
          animation. Treat the preview as a fast check, then confirm unusual parts in game.
        </Callout>
      </>
    ),
  },
  {
    id: "ink-lab",
    label: "Ink Lab",
    icon: <Syringe className="h-5 w-5" />,
    shortDescription: "Turn a prompt, reference or your own PNG into a drop-in FiveM tattoo addon.",
    badge: "Studio",
    seoTitle: "Ink Lab — Build FiveM Tattoo Packs",
    seoDescription:
      "Generate or import tattoo art, place it on a freemode body, preview it, and export a ready-to-install FiveM tattoo resource as a ZIP.",
    keywords: [
      "FiveM tattoo creator",
      "FiveM tattoo pack",
      "GTA5 tattoo overlay",
      "shop_tattoo.meta",
      "freemode tattoo",
    ],
    toolHref: "/ink-lab",
    faqs: [
      {
        q: "What is the difference between Single tattoo and Tattoo pack?",
        a: "Single tattoo is for one design. Tattoo pack lets you collect many tattoos, place each one and export them together as one resource. The Ink Lab mode switch at the top changes between them.",
      },
      {
        q: "What is the difference between Tattoo overlays and Painted into skin?",
        a: "Tattoo overlays is the standard export: the tattoos appear in the tattoo shop as overlays. Painted into skin bakes the tattoo into the body's skin textures instead, and is experimental. It needs your own freemode body files.",
      },
      {
        q: "Does generating art cost credits?",
        a: "Yes. Generate with AI shows the cost before you run it: 100 credits per version, and you can ask for up to 3 versions at once. A version that fails is refunded. Importing your own PNG, JPG or WebP costs nothing.",
      },
      {
        q: "Can I preview on my own body?",
        a: "Yes. Use my own body takes your freemode body .ydd files, plus the optional skin .ytd files, for a preview that matches the game. Back to built-in body returns to the default mannequin.",
      },
      {
        q: "How do I install the result?",
        a: "Unzip the export into your server's resources folder and ensure it. The tattoos then appear in the tattoo shop.",
      },
      {
        q: "Who can use Ink Lab?",
        a: "Ink Lab is a Studio feature. Studio plan members get new tools first.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Ink Lab turns a tattoo idea into a FiveM addon. Generate the art from a prompt or reference,
          or upload your own PNG, choose the body zone, preview it on a freemode body and export a resource you
          can drop onto your server. It is included with the Studio plan.
        </p>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Pick Single tattoo for one design or Tattoo pack for many. In a pack, use Add pictures to import art, or Generate with AI to make it.",
            "For AI art, describe the tattoo, pick a Style and Colour, optionally add a Reference image, and choose how many versions to make.",
            "Choose the Gender and Body zone. This decides where the tattoo goes in game.",
            "Check the preview. Tune Remove background and Edge tolerance until the art is a clean cutout.",
            "In a pack, use the Sheet tab to lay tattoos out on a zone and the 3D tab to see them on a body. Adjust Scale, Rotation, UV X and UV Y for the selected tattoo.",
            "Open Export, choose Tattoo overlays or Painted into skin, set the pack name, and click Export pack (.zip).",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Useful controls</p>
        <BulletList
          items={[
            "Ink tint recolors the art: Original leaves it alone, Dark ink only recolors the line work, and All ink recolors everything.",
            "The Tattoo panel shows the Shop slot the tattoo will use. If two tattoos land in the same slot, set a Shop slot override so both stay in the shop.",
            "Texture size in the export panel offers 512 or 1024.",
            "Duplicate, rename, hide or remove a tattoo from the tattoo list. Undo and Redo cover your edits.",
            "Your pack is saved on this device, so you can reopen it from Open a recent pack.",
            "Under Export, Advanced lets you copy or download the tattoo XML after the pack is exported.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Painted into skin</p>
        <p className="text-sm leading-7 text-muted-foreground">
          This export bakes each tattoo into the skin textures instead of using a tattoo overlay.
          Load your freemode body drawables (.ydd) and skin .ytd files, place the tattoos on the
          Body tab, then export. It is experimental, so test it on a server before you rely on it.
        </p>
        <Callout type="tip">
          Use a high-contrast design on a transparent or plain background. Background removal
          works best when the tattoo is clearly separated from what is behind it.
        </Callout>
      </>
    ),
  },
];
